import React, { useState, useEffect } from 'react';
import { db } from '../../config/firebase';
import {
  collection, getDocs, addDoc, updateDoc,
  deleteDoc, doc, serverTimestamp
} from 'firebase/firestore';
import Sidebar from './Sidebar';
import { useAuth } from '../../context/AuthContext';

const EMPTY_FORM = {
  title: '', date: '', meetingType: 'Staff Meeting',
  attendees: '', agenda: '', minutesText: '', actionItems: '',
};

const MEETING_TYPES = ['Staff Meeting', 'Board Meeting', 'Parent Meeting', 'Community Meeting', 'Donor Meeting', 'Other'];

export default function Meetings() {
  const { currentUser, userRole } = useAuth();
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [filterType, setFilterType] = useState('all');

  useEffect(() => { fetchMeetings(); }, []);

  async function fetchMeetings() {
    try {
      const snap = await getDocs(collection(db, 'meetings'));
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      data.sort((a, b) => (b.date || '').localeCompare(a.date || ''));
      setMeetings(data);
    } catch (err) { console.error(err); }
  }

  function handleChange(e) {
    setForm({ ...form, [e.target.name]: e.target.value });
  }

  function handleCancel() {
    setShowForm(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      if (editingId) {
        await updateDoc(doc(db, 'meetings', editingId), {
          ...form,
          updatedAt: serverTimestamp(),
          updatedBy: currentUser.uid,
          updatedByName: currentUser.displayName || currentUser.email,
        });
        setSuccess('Meeting minutes updated!');
      } else {
        await addDoc(collection(db, 'meetings'), {
          ...form,
          createdBy: currentUser.uid,
          createdByName: currentUser.displayName || currentUser.email || 'Staff',
          createdAt: serverTimestamp(),
        });
        setSuccess('Meeting minutes saved!');
      }
      handleCancel();
      fetchMeetings();
    } catch (err) {
      console.error(err);
      setError('Something went wrong. Please try again.');
    }
    setLoading(false);
  }

  function handleEdit(m) {
    setEditingId(m.id);
    setForm({
      title: m.title || '',
      date: m.date || '',
      meetingType: m.meetingType || 'Staff Meeting',
      attendees: m.attendees || '',
      agenda: m.agenda || '',
      minutesText: m.minutesText || '',
      actionItems: m.actionItems || '',
    });
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  async function handleDelete(id) {
    if (window.confirm('Are you sure you want to delete these meeting minutes?')) {
      await deleteDoc(doc(db, 'meetings', id));
      fetchMeetings();
    }
  }

  function canEdit(m) {
    return userRole === 'admin' || m.createdBy === currentUser?.uid;
  }

  const filtered = filterType === 'all'
    ? meetings
    : meetings.filter(m => m.meetingType === filterType);

  return (
    <div style={styles.layout}>
      <Sidebar />
      <div style={styles.main}>

        <div style={styles.pageHeader}>
          <div>
            <div style={styles.pageTitle}>Meeting Minutes</div>
            <div style={styles.pageSub}>
              {userRole === 'admin'
                ? 'View and open all recorded vikao'
                : 'Record and manage vikao minutes'}
            </div>
          </div>
          <button
            style={styles.btnPrimary}
            onClick={() => (showForm ? handleCancel() : setShowForm(true))}
          >
            {showForm ? 'Cancel' : '+ New Minutes'}
          </button>
        </div>

        {success && <div style={styles.successMsg}>{success}</div>}
        {error && <div style={styles.errorMsg}>{error}</div>}

        {showForm && (
          <div style={styles.panel}>
            <div style={styles.sectionTitle}>
              {editingId ? 'Edit Meeting Minutes' : 'Record New Meeting Minutes'}
            </div>
            <form onSubmit={handleSubmit}>
              <div style={styles.formGrid}>
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Meeting Title *</label>
                  <input style={styles.formInput} name="title" value={form.title} onChange={handleChange} placeholder="e.g. Monthly Staff Meeting - July" required />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Date *</label>
                  <input style={styles.formInput} name="date" type="date" value={form.date} onChange={handleChange} required />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Meeting Type</label>
                  <select style={styles.formInput} name="meetingType" value={form.meetingType} onChange={handleChange}>
                    {MEETING_TYPES.map(t => <option key={t}>{t}</option>)}
                  </select>
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Attendees</label>
                  <input style={styles.formInput} name="attendees" value={form.attendees} onChange={handleChange} placeholder="e.g. Amina, John, Grace" />
                </div>
                <div style={{ ...styles.formGroup, gridColumn: '1 / -1' }}>
                  <label style={styles.formLabel}>Agenda</label>
                  <textarea style={{ ...styles.formInput, height: '70px', resize: 'vertical' }} name="agenda" value={form.agenda} onChange={handleChange} placeholder="What was this meeting about?" />
                </div>
                <div style={{ ...styles.formGroup, gridColumn: '1 / -1' }}>
                  <label style={styles.formLabel}>Minutes *</label>
                  <textarea style={{ ...styles.formInput, height: '140px', resize: 'vertical' }} name="minutesText" value={form.minutesText} onChange={handleChange} placeholder="What was discussed and decided..." required />
                </div>
                <div style={{ ...styles.formGroup, gridColumn: '1 / -1' }}>
                  <label style={styles.formLabel}>Action Items / Follow-ups</label>
                  <textarea style={{ ...styles.formInput, height: '70px', resize: 'vertical' }} name="actionItems" value={form.actionItems} onChange={handleChange} placeholder="Who needs to do what, by when..." />
                </div>
              </div>
              <button type="submit" style={styles.submitBtn} disabled={loading}>
                {loading
                  ? (editingId ? 'Updating...' : 'Saving...')
                  : (editingId ? 'Update Minutes' : 'Save Minutes')}
              </button>
            </form>
          </div>
        )}

        {/* FILTER */}
        <div style={styles.filterRow}>
          <button
            style={{ ...styles.filterBtn, ...(filterType === 'all' ? styles.filterActive : {}) }}
            onClick={() => setFilterType('all')}
          >
            All ({meetings.length})
          </button>
          {MEETING_TYPES.map(t => {
            const count = meetings.filter(m => m.meetingType === t).length;
            if (count === 0) return null;
            return (
              <button
                key={t}
                style={{ ...styles.filterBtn, ...(filterType === t ? styles.filterActive : {}) }}
                onClick={() => setFilterType(t)}
              >
                {t} ({count})
              </button>
            );
          })}
        </div>

        {filtered.length === 0 ? (
          <div style={styles.panel}>
            <div style={styles.empty}>No meeting minutes recorded yet.</div>
          </div>
        ) : (
          filtered.map(m => {
            const isExpanded = expandedId === m.id;
            return (
              <div key={m.id} style={styles.meetingCard}>
                <div style={styles.meetingHeader} onClick={() => setExpandedId(isExpanded ? null : m.id)}>
                  <div style={styles.meetingIcon}></div>
                  <div style={styles.meetingInfo}>
                    <div style={styles.meetingTitle}>{m.title}</div>
                    <div style={styles.meetingMeta}>
                      {m.date} · {m.meetingType} · by {m.createdByName || 'Staff'}
                    </div>
                  </div>
                  <span style={styles.expandTag}>{isExpanded ? '▲ Close' : '▼ Open'}</span>
                </div>

                {isExpanded && (
                  <div style={styles.meetingBody}>
                    {m.attendees && (
                      <div style={styles.meetingSection}>
                        <div style={styles.meetingLabel}>Attendees</div>
                        <div style={styles.meetingText}>{m.attendees}</div>
                      </div>
                    )}
                    {m.agenda && (
                      <div style={styles.meetingSection}>
                        <div style={styles.meetingLabel}>Agenda</div>
                        <div style={styles.meetingText}>{m.agenda}</div>
                      </div>
                    )}
                    <div style={styles.meetingSection}>
                      <div style={styles.meetingLabel}>Minutes</div>
                      <div style={styles.meetingText}>{m.minutesText}</div>
                    </div>
                    {m.actionItems && (
                      <div style={styles.meetingSection}>
                        <div style={styles.meetingLabel}>Action Items</div>
                        <div style={styles.meetingText}>{m.actionItems}</div>
                      </div>
                    )}
                    {canEdit(m) && (
                      <div style={styles.meetingActions}>
                        <button style={styles.btnEdit} onClick={() => handleEdit(m)}>Edit</button>
                        <button style={styles.btnDelete} onClick={() => handleDelete(m.id)}>Delete</button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

const styles = {
  layout: { display: 'flex', minHeight: '100vh', background: '#F6F4EE' },
  main: { flex: 1, padding: '26px', overflowY: 'auto' },
  pageHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px', flexWrap: 'wrap', gap: '14px' },
  pageTitle: { fontFamily: "'Fraunces', Georgia, serif", fontSize: '25px', background: 'linear-gradient(135deg, #14452F, #1F6B45)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' },
  pageSub: { fontSize: '12.5px', color: '#6B7280', marginTop: '3px' },
  btnPrimary: { background: 'linear-gradient(135deg, #F57C00, #FFA726)', color: '#fff', border: 'none', padding: '10px 20px', borderRadius: '10px', fontFamily: 'inherit', fontSize: '13px', fontWeight: '600', cursor: 'pointer', boxShadow: '0 4px 14px rgba(245,124,0,0.25)' },
  successMsg: { background: '#EAF3EC', border: '1px solid #BBD8C4', color: '#14452F', padding: '12px 16px', borderRadius: '10px', fontSize: '13px', marginBottom: '20px' },
  errorMsg: { background: '#FFEBEE', border: '1px solid #FFCDD2', color: '#C62828', padding: '12px 16px', borderRadius: '10px', fontSize: '13px', marginBottom: '20px' },
  panel: { background: '#fff', borderRadius: '16px', padding: '20px', boxShadow: '0 2px 10px rgba(0,0,0,0.05)', border: '1px solid rgba(0,0,0,0.04)', marginBottom: '20px' },
  sectionTitle: { fontSize: '11px', letterSpacing: '0.8px', textTransform: 'uppercase', color: '#6B7280', marginBottom: '14px', paddingBottom: '8px', borderBottom: '1px solid #F3F4F6', fontWeight: '600' },
  formGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '16px' },
  formGroup: { display: 'flex', flexDirection: 'column', gap: '5px' },
  formLabel: { fontSize: '12px', color: '#6B7280', fontWeight: '500' },
  formInput: { padding: '11px 14px', borderRadius: '9px', border: '1.5px solid #E5E7EB', background: '#FAF9F5', fontFamily: 'inherit', fontSize: '13.5px', color: '#1C1C1E', outline: 'none' },
  submitBtn: { width: '100%', padding: '13px', background: 'linear-gradient(135deg, #14452F, #1F6B45)', color: '#fff', border: 'none', borderRadius: '10px', fontFamily: 'inherit', fontSize: '14px', fontWeight: '600', cursor: 'pointer', boxShadow: '0 4px 14px rgba(20,69,47,0.25)' },
  empty: { textAlign: 'center', color: '#6B7280', fontSize: '13px', padding: '20px 0' },
  filterRow: { display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' },
  filterBtn: { padding: '8px 16px', borderRadius: '20px', border: '1.5px solid #E5E7EB', background: 'transparent', color: '#6B7280', fontSize: '12.5px', cursor: 'pointer', fontFamily: 'inherit' },
  filterActive: { background: 'linear-gradient(135deg, #14452F, #1F6B45)', borderColor: 'transparent', color: '#fff' },
  meetingCard: { background: '#fff', borderRadius: '16px', boxShadow: '0 2px 10px rgba(0,0,0,0.05)', border: '1px solid rgba(0,0,0,0.04)', marginBottom: '12px', overflow: 'hidden' },
  meetingHeader: { display: 'flex', alignItems: 'center', gap: '12px', padding: '16px 20px', cursor: 'pointer', flexWrap: 'wrap' },
  meetingIcon: { width: '40px', height: '40px', borderRadius: '10px', background: 'linear-gradient(135deg, #E3F2FD, #BBDEFB)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px', flexShrink: 0 },
  meetingInfo: { flex: 1, minWidth: '160px' },
  meetingTitle: { fontSize: '15px', fontWeight: '700', color: '#1C1C1E' },
  meetingMeta: { fontSize: '11.5px', color: '#6B7280', marginTop: '2px' },
  expandTag: { fontSize: '12px', color: '#1565C0', fontWeight: '600' },
  meetingBody: { padding: '0 20px 20px', borderTop: '1px solid #F3F4F6' },
  meetingSection: { marginTop: '14px' },
  meetingLabel: { fontSize: '11.5px', fontWeight: '700', color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '5px' },
  meetingText: { fontSize: '13.5px', color: '#374151', lineHeight: 1.6, whiteSpace: 'pre-wrap' },
  meetingActions: { display: 'flex', gap: '10px', marginTop: '18px' },
  btnEdit: { background: '#E3F2FD', color: '#1565C0', border: 'none', padding: '9px 16px', borderRadius: '9px', fontSize: '12.5px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit' },
  btnDelete: { background: '#FFEBEE', color: '#C62828', border: 'none', padding: '9px 16px', borderRadius: '9px', fontSize: '12.5px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit' },
};
